// Klinik geneli tıbbi kayıt listelerinin düz sayfa iskeleti: beyaz zemin,
// başlık şeridi (PageHeaderBar) → araç satırı (arama + ek filtre) → hazır filtre
// çipleri (sayılı) → koşullu bilgi şeridi → tablo (kalan alanı doldurur).
// Kurallar: docs/design-principles.md §3

import React from "react";
import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { ChoiceChipGroup, SearchField } from "../../atoms";
import { PageHeaderBar } from "../../molecules";
import { COLORS, TYPO } from "../../../styles/uiConstants";

/**
 * @param {React.ReactNode} icon
 * @param {string} title
 * @param {string} [subtitle]
 * @param {React.ReactNode} [action] - başlık şeridinin sağındaki birincil aksiyon (ör. "Klinik Oluştur")
 * @param {string} search
 * @param {(v: string) => void} onSearchChange
 * @param {string} searchPlaceholder
 * @param {React.ReactNode} [filters] - aramanın yanındaki ek filtre atom'ları
 * @param {{value: string, label: string}[]} segments - hazır filtreler (etiket sayılı)
 * @param {string} segment
 * @param {(v: string) => void} onSegmentChange
 * @param {string} segmentAriaLabel
 * @param {{icon, text, actionLabel, onClick}} [notice] - yalnız içerik varken görünen bilgi şeridi
 * @param {React.ReactNode} children - tablo
 */
export default function ClinicRecordListLayout({
  icon,
  title,
  subtitle,
  action,
  search,
  onSearchChange,
  searchPlaceholder,
  filters,
  segments,
  segment,
  onSegmentChange,
  segmentAriaLabel,
  notice,
  children,
}) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: COLORS.white,
        minHeight: 0,
      }}
    >
      <PageHeaderBar icon={icon} title={title} subtitle={subtitle} action={action} />

      {/* Araç satırı */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          pt: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <SearchField
          placeholder={searchPlaceholder}
          value={search}
          onChange={onSearchChange}
          autoComplete="off"
          sx={{ width: { xs: "100%", sm: 340 } }}
        />
        {filters}
      </Box>

      {/* Hazır filtreler */}
      <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 2, flexShrink: 0 }}>
        <ChoiceChipGroup
          ariaLabel={segmentAriaLabel}
          options={segments}
          value={segment}
          onChange={onSegmentChange}
        />
      </Box>

      {/* Bilgi şeridi — yalnız içerik varken */}
      {notice ? (
        <ButtonBase
          onClick={notice.onClick}
          sx={{
            mx: { xs: 2, md: 4 },
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            justifyContent: "flex-start",
            gap: 1,
            bgcolor: COLORS.warningLightest,
            border: `1px solid ${alpha(COLORS.warning, 0.3)}`,
            flexShrink: 0,
          }}
        >
          {notice.icon ? (
            <Box component="span" sx={{ display: "inline-flex", color: COLORS.warningDark, "& svg": { fontSize: 16 } }}>
              {notice.icon}
            </Box>
          ) : null}
          <Typography sx={{ fontSize: TYPO.body, color: COLORS.warningDarker, flex: 1, textAlign: "left" }}>
            {notice.text}
          </Typography>
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.warningDarker }}>
            {notice.actionLabel}
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 18, color: COLORS.warningDarker }} />
        </ButtonBase>
      ) : null}

      {/* Tablo — kalan alanı doldurur, kendi içinde kayar */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>{children}</Box>
    </Box>
  );
}
